import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import utils from '../../utils';
import cartServices from '../../services/CartServices';
import Loading from '../../components/feedback/Loading';
import FetchError from '../../components/feedback/FetchError';
import Empty from '../../components/feedback/Empty';
import CartItem from '../../components/cart/CartItem';
import { useCart } from '../../context/CartContext';

function CartPage() {
    const {
        numCartItems,
        setNumItems,
        setItemSlugs,
    } = useCart();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const totalPrice = items.reduce((acc, item) => acc + parseFloat(item?.total || 0), 0);

    useEffect(() => {
        fetchCart();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCart = () => {
        setLoading(true);
        setError(null);
        cartServices.getCartItems()
            .then(res => {
                const data = res.data || [];
                setItems(data);
                // Sync with context (optional — backend may be source of truth)
                // But good for instant UI feedback
                setNumItems(data.reduce((sum, item) => sum + (item.quantity || 0), 0));
                setItemSlugs(data.map(item => ({
                    slug: item.product?.slug,
                    id: item.product?.id
                })));
                setLoading(false);
            })
            .catch(err => {
                console.error('Cart fetch error:', err);
                setError(err);
                setLoading(false);
            });
    };

    const handleDeleteItem = (item) => {
        setLoading(true);
        const payload = { id: item?.id };
        cartServices.removeCartItem(payload)
            .then(res => {
                const data = res.data;
                const newItems = data?.cart_items || [];
                setItems(newItems);

                // ✅ Sync context
                const newNum = newItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
                const newSlugs = newItems.map(i => ({
                    slug: i.product?.slug,
                    id: i.product?.id
                }));

                setNumItems(newNum);
                setItemSlugs(newSlugs);

                toast.success('Item removed from cart');
                setLoading(false);
                fetchCart()
            })
            .catch(err => {
                setLoading(false);
                const msg = err.response?.data?.message || 'Failed to remove item';
                toast.error(msg);
            });
    };

    const handleUpdateQuantity = (item, newQuantity) => {
        setLoading(true);
        const payload = {
            product: item?.product?.id,
            quantity: newQuantity
        };
        cartServices.addCartItem(payload)
            .then(res => {
                const data = res.data;
                const newItems = data?.cart_items || [];
                setItems(newItems);

                const newNum = newItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
                const newSlugs = newItems.map(i => ({
                    slug: i.product?.slug,
                    id: i.product?.id
                }));

                setNumItems(newNum);
                setItemSlugs(newSlugs);

                toast.success('Cart updated');
                setLoading(false);
                fetchCart()
            })
            .catch(err => {
                setLoading(false);
                const msg = err.response?.data?.message || 'Failed to update quantity';
                toast.error(msg);
            });
    };

    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <Loading />
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-10">
                <FetchError error={error} retry={fetchCart} />
            </div>
        );
    }

    return (
        <div className="">
            <Helmet>
                <title>Shopping Cart - {utils.siteName}</title>
                <meta name="description" content="Review your selected items, update quantities, and proceed to checkout." />
                <meta name="robots" content="noindex" />
            </Helmet>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="mb-8">
                    <h1 className="text-4xl font-black text-primary dark:text-secondary">Shopping Cart</h1>
                    {!items?.length && (
                        <div className='mt-3'>
                            <p className="font-semibold">
                                {numCartItems} {numCartItems === 1 ? 'item' : 'items'} in your cart
                            </p>
                        </div>
                    )}
                </div>

                {items.length === 0 ? (
                    <div className="">
                        <Empty objectName="Cart Items" />
                        <div className="text-center mt-6">
                            <Link
                                to="/products"
                                className="inline-block px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-red-800 transition"
                            >
                                Continue Shopping
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col lg:flex-row gap-8">
                        <div className="w-full lg:w-2/3">
                            <div className="flex justify-between items-center border-b border-border-light dark:border-border-dark pb-4 mb-6">
                                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                    Your Items ({numCartItems})
                                </h2>
                                <Link
                                    to="/products"
                                    className="text-sm font-medium text-primary hover:underline"
                                >
                                    Continue Shopping
                                </Link>
                            </div>

                            <div className="space-y-6">
                                {items.map((item, idx) => (
                                    <CartItem
                                        key={item.id || idx}
                                        idx={idx}
                                        item={item}
                                        deleteItem={() => handleDeleteItem(item)}
                                        updateItemQuantity={handleUpdateQuantity}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Order Summary (Sticky) */}
                        <div className="w-full lg:w-1/3">
                            <div className="bg-white dark:bg-card-dark rounded-xl shadow-md border border-border-light dark:border-border-dark p-6 sticky top-6">
                                <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-border-light dark:border-border-dark pb-4 mb-4">
                                    Order Summary
                                </h2>

                                <div className="space-y-3 mb-6">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Subtotal ({numCartItems} item{numCartItems !== 1 ? 's' : ''})</span>
                                        <span className="font-medium">KSH {utils.toCommas(totalPrice)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Delivery</span>
                                        <span className="font-medium">Calculated at checkout</span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-border-light dark:border-border-dark">
                                        <span className="font-bold text-lg">Total</span>
                                        <span className="font-bold text-lg text-secondary">KSH {utils.toCommas(totalPrice)}</span>
                                    </div>
                                </div>

                                <Link
                                    to="/cart/checkout"
                                    className="w-full block bg-secondary text-white font-bold py-3 px-4 rounded-lg text-center hover:bg-orange-600 transition shadow-md"
                                >
                                    Proceed to Checkout
                                </Link>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
                                    Delivery calculated at checkout
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CartPage;